import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  Pause,
  XCircle,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Loader2,
  Database,
  GitBranch,
  User,
  Calendar,
  Settings,
  ChevronRight,
  ChevronLeft,
  Activity,
  List,
  Eye,
  Edit,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
  TrendingUp,
  Layers,
  Search,
  Hash,
  RotateCcw
} from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { toast } from 'sonner';
import { getCamundaService } from '../../infrastructure/camunda';
import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';

interface ProcessInstanceDetails {
  id: string;
  businessKey: string | null;
  processDefinitionId: string;
  processDefinitionKey: string;
  processDefinitionName: string;
  startTime: string;
  endTime: string | null;
  state: string;
  suspended: boolean;
  tenantId: string | null;
  startUserId: string | null;
  version?: number;
}

interface ActivityInstance {
  id: string;
  activityId: string;
  activityName: string;
  activityType: string;
  startTime: string;
  endTime: string | null;
  durationInMillis: number | null;
  canceled: boolean;
  completeScope: boolean;
}

interface Variable {
  name: string;
  type: string;
  value: any;
  valueInfo: any;
}

interface Incident {
  id: string;
  incidentType: string;
  incidentMessage: string;
  activityId: string;
  createTime: string;
}

interface Task {
  id: string;
  name: string;
  assignee: string | null;
  created: string;
  due: string | null;
  priority: number;
  taskDefinitionKey: string;
}

const ProcessInstancePage: React.FC = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();

  const [instance, setInstance] = useState<ProcessInstanceDetails | null>(null);
  const [activities, setActivities] = useState<ActivityInstance[]>([]);
  const [variables, setVariables] = useState<Variable[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'diagram' | 'bpmn' | 'activities' | 'variables' | 'incidents' | 'tasks'>('diagram');
  const [suspending, setSuspending] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // New state for enhanced UI
  const [zoom, setZoom] = useState(100);
  const [variableSearch, setVariableSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVariableCount, setTotalVariableCount] = useState(0);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const itemsPerPage = 5;

  // BPMN Diagram state
  const [bpmnXml, setBpmnXml] = useState<string | null>(null);
  const bpmnContainerRef = React.useRef<HTMLDivElement>(null);
  const bpmnViewerRef = React.useRef<any>(null);

  const camunda = getCamundaService();

  // Calculate duration
  const calculateDuration = (startTime: string, endTime: string | null): string => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  // Calculate progress
  const calculateProgress = (): number => {
    if (!instance) return 0;
    if (instance.state === 'COMPLETED') return 100;
    if (instance.state === 'EXTERNALLY_TERMINATED' || instance.state === 'INTERNALLY_TERMINATED') return 100;

    // Use activities list to calculate progress
    const completedActivities = activities.filter(a => a.endTime !== null).length;
    // Estimate total based on history or definition if available, otherwise just use current count
    const totalActivities = activities.length || 1;

    return Math.min(Math.round((completedActivities / totalActivities) * 100), 95);
  };

  // Get completed, active, and remaining counts
  const getActivityCounts = () => {
    const completed = activities.filter(a => a.endTime !== null && !a.canceled).length;
    const active = activities.filter(a => a.endTime === null).length;
    const remaining = Math.max(0, activities.length - completed - active);
    return { completed, active, remaining };
  };

  // Filter variables based on search (client-side filter on current page)
  const filteredVariables = variableSearch
    ? variables.filter(variable =>
      variable.name.toLowerCase().includes(variableSearch.toLowerCase()) ||
      variable.type.toLowerCase().includes(variableSearch.toLowerCase()) ||
      String(variable.value).toLowerCase().includes(variableSearch.toLowerCase())
    )
    : variables;
  const totalPages = Math.ceil(totalVariableCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Fetch instance details
  const fetchInstanceDetails = useCallback(async () => {
    if (!instanceId) return;

    try {
      setLoading(true);

      // Fetch historic process instance
      const historyResponse = await camunda.history.getProcessInstances({
        processInstanceId: instanceId,
      });

      if (historyResponse.length === 0) {
        toast.error('Process instance not found');
        navigate('/processes');
        return;
      }

      const histInstance = historyResponse[0];

      // Get process definition details
      let processDefName = histInstance.processDefinitionKey;
      let version = 1;
      try {
        const processDef = await camunda.processDefinition.getById(histInstance.processDefinitionId);
        processDefName = processDef.name || processDef.key;
        version = processDef.version;
      } catch {
        // Use key if name fetch fails
      }

      setInstance({
        id: histInstance.id,
        businessKey: histInstance.businessKey,
        processDefinitionId: histInstance.processDefinitionId,
        processDefinitionKey: histInstance.processDefinitionKey,
        processDefinitionName: processDefName,
        startTime: histInstance.startTime,
        endTime: histInstance.endTime,
        state: histInstance.state,
        suspended: histInstance.state === 'SUSPENDED',
        tenantId: histInstance.tenantId,
        startUserId: histInstance.startUserId,
        version,
      });

      // Fetch activity instances
      try {
        const activityResponse = await camunda.history.getActivityInstances({
          processInstanceId: instanceId,
          sortBy: 'startTime',
          sortOrder: 'asc',
        });
        setActivities(activityResponse.map((a: any) => ({
          id: a.id,
          activityId: a.activityId,
          activityName: a.activityName || a.activityId,
          activityType: a.activityType,
          startTime: a.startTime,
          endTime: a.endTime,
          durationInMillis: a.durationInMillis,
          canceled: a.canceled,
          completeScope: a.completeScope,
        })));
      } catch (e) {
        console.error('Error fetching activities:', e);
      }

      // Fetch BPMN XML
      try {
        const xmlResponse = await camunda.processDefinition.getXmlById(histInstance.processDefinitionId);
        setBpmnXml(xmlResponse.bpmn20Xml);
      } catch (e) {
        console.error('Error fetching BPMN XML:', e);
      }

      // Fetch variable count first
      try {
        const countResponse = await camunda.history.getVariableInstancesCount({
          processInstanceId: instanceId,
        });
        setTotalVariableCount(countResponse.count || 0);

        // Fetch first page of variables
        const variableResponse = await camunda.history.getVariableInstances({
          processInstanceId: instanceId,
          firstResult: 0,
          maxResults: itemsPerPage,
        });
        setVariables(variableResponse.map((v: any) => ({
          name: v.name,
          type: v.type,
          value: v.value,
          valueInfo: v.valueInfo,
        })));
      } catch (e) {
        console.error('Error fetching variables:', e);
      }

      // Fetch incidents
      try {
        const incidentResponse = await camunda.incident.getList({
          processInstanceId: instanceId,
        });
        setIncidents(incidentResponse.map((i: any) => ({
          id: i.id,
          incidentType: i.incidentType,
          incidentMessage: i.incidentMessage,
          activityId: i.activityId,
          createTime: i.incidentTimestamp,
        })));
      } catch (e) {
        console.error('Error fetching incidents:', e);
      }

      // Fetch tasks (only for running instances)
      if (histInstance.state === 'ACTIVE') {
        try {
          const taskResponse = await camunda.task.getList({
            processInstanceId: instanceId,
          });
          setTasks(taskResponse.map((t: any) => ({
            id: t.id,
            name: t.name,
            assignee: t.assignee,
            created: t.created,
            due: t.due,
            priority: t.priority,
            taskDefinitionKey: t.taskDefinitionKey,
          })));
        } catch (e) {
          console.error('Error fetching tasks:', e);
        }
      }

    } catch (error) {
      console.error('Error fetching instance details:', error);
      toast.error('Failed to fetch process instance details');
    } finally {
      setLoading(false);
    }
  }, [instanceId, camunda, navigate]);

  useEffect(() => {
    fetchInstanceDetails();
  }, [fetchInstanceDetails]);

  // Fetch variables for a specific page (server-side pagination)
  const fetchVariablesPage = useCallback(async (page: number) => {
    if (!instanceId) return;

    try {
      setVariablesLoading(true);
      const variableResponse = await camunda.history.getVariableInstances({
        processInstanceId: instanceId,
        firstResult: (page - 1) * itemsPerPage,
        maxResults: itemsPerPage,
      });
      setVariables(variableResponse.map((v: any) => ({
        name: v.name,
        type: v.type,
        value: v.value,
        valueInfo: v.valueInfo,
      })));
    } catch (e) {
      console.error('Error fetching variables page:', e);
    } finally {
      setVariablesLoading(false);
    }
  }, [instanceId, camunda, itemsPerPage]);

  // Refetch variables when page changes (only after initial load)
  useEffect(() => {
    if (!loading && currentPage > 1) {
      fetchVariablesPage(currentPage);
    }
  }, [currentPage, loading, fetchVariablesPage]);


  // Initialize BPMN Viewer and add overlays
  useEffect(() => {
    if (activeTab !== 'bpmn' || !bpmnXml || !bpmnContainerRef.current) return;

    // Clear previous viewer instance if exists
    if (bpmnViewerRef.current) {
      bpmnViewerRef.current.destroy();
    }

    const viewer = new NavigatedViewer({
      container: bpmnContainerRef.current,
    });
    bpmnViewerRef.current = viewer;

    const loadBpmn = async () => {
      try {
        await viewer.importXML(bpmnXml);

        // Zoom to fit
        // Zoom to fit
        const canvas = viewer.get('canvas') as any;
        canvas.zoom('fit-viewport');

        // Add overlays
        const overlays = viewer.get('overlays') as any;

        // Group activities by ID to count running instances
        const activityCounts: Record<string, { active: number, completed: number, incidents: number }> = {};

        activities.forEach(activity => {
          if (!activityCounts[activity.activityId]) {
            activityCounts[activity.activityId] = { active: 0, completed: 0, incidents: 0 };
          }

          if (activity.endTime === null && !activity.canceled) {
            activityCounts[activity.activityId].active++;
          } else if (activity.endTime !== null && !activity.canceled) {
            activityCounts[activity.activityId].completed++;
          }
        });

        incidents.forEach(incident => {
          if (!activityCounts[incident.activityId]) {
            activityCounts[incident.activityId] = { active: 0, completed: 0, incidents: 0 };
          }
          activityCounts[incident.activityId].incidents++;
        });

        // Add overlays for each activity
        Object.entries(activityCounts).forEach(([activityId, counts]) => {
          try {
            // Check if element exists in diagram
            const elementRegistry = viewer.get('elementRegistry') as any;
            if (!elementRegistry.get(activityId)) return;

            if (counts.incidents > 0) {
              overlays.add(activityId, {
                position: {
                  bottom: 10,
                  right: 10
                },
                html: `<div class="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md border-2 border-white">${counts.incidents}</div>`
              });
            }

            if (counts.active > 0) {
              overlays.add(activityId, {
                position: {
                  bottom: 15,
                  left: -10
                },
                html: `<div class="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md border-2 border-white animate-bounce">${counts.active}</div>`
              });

              // Highlight active element
              canvas.addMarker(activityId, 'highlight-active');
            } else if (counts.completed > 0) {
              // Only show completed badge if no active instances
              overlays.add(activityId, {
                position: {
                  bottom: 15,
                  left: -10
                },
                html: `<div class="bg-gray-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md border-2 border-white">${counts.completed}</div>`
              });
            }
          } catch (e) {
            console.warn(`Could not add overlay for activity ${activityId}`, e);
          }
        });

      } catch (err) {
        console.error('Error rendering BPMN:', err);
        toast.error('Failed to render BPMN diagram');
      }
    };

    loadBpmn();

    return () => {
      if (bpmnViewerRef.current) {
        bpmnViewerRef.current.destroy();
        bpmnViewerRef.current = null;
      }
    };
  }, [activeTab, bpmnXml, activities, incidents]);
  const handleSuspend = async () => {
    if (!instance) return;

    try {
      setSuspending(true);
      await camunda.processInstance.updateSuspensionStateById(instance.id, !instance.suspended);
      toast.success(instance.suspended ? 'Process instance activated' : 'Process instance suspended');
      fetchInstanceDetails();
    } catch (error) {
      console.error('Error suspending/activating instance:', error);
      toast.error('Failed to suspend/activate process instance');
    } finally {
      setSuspending(false);
    }
  };

  // Cancel instance
  const handleCancel = async () => {
    if (!instance) return;

    if (!confirm('Are you sure you want to cancel this process instance? This action cannot be undone.')) {
      return;
    }

    try {
      setCanceling(true);
      await camunda.processInstance.deleteById(instance.id);
      toast.success('Process instance canceled');
      navigate('/processes');
    } catch (error) {
      console.error('Error canceling instance:', error);
      toast.error('Failed to cancel process instance');
    } finally {
      setCanceling(false);
    }
  };

  const getStatusColor = (state: string, suspended: boolean) => {
    if (suspended) return 'bg-yellow-50 text-yellow-700';
    switch (state) {
      case 'ACTIVE':
        return 'bg-blue-50 text-blue-700';
      case 'COMPLETED':
        return 'bg-green-50 text-green-700';
      case 'EXTERNALLY_TERMINATED':
      case 'INTERNALLY_TERMINATED':
        return 'bg-gray-100 text-gray-700';
      case 'SUSPENDED':
        return 'bg-yellow-50 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (state: string, suspended: boolean) => {
    if (suspended) return 'Suspended';
    switch (state) {
      case 'ACTIVE':
        return 'Active';
      case 'COMPLETED':
        return 'Completed';
      case 'EXTERNALLY_TERMINATED':
      case 'INTERNALLY_TERMINATED':
        return 'Canceled';
      case 'SUSPENDED':
        return 'Suspended';
      default:
        return state;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'startEvent':
        return <Play size={14} className="text-green-500" />;
      case 'endEvent':
        return <CheckCircle size={14} className="text-green-500" />;
      case 'userTask':
        return <User size={14} className="text-blue-500" />;
      case 'serviceTask':
        return <Settings size={14} className="text-purple-500" />;
      case 'exclusiveGateway':
      case 'parallelGateway':
        return <GitBranch size={14} className="text-orange-500" />;
      default:
        return <Activity size={14} className="text-gray-500" />;
    }
  };

  const formatValue = (value: any, type: string): string => {
    if (value === null || value === undefined) return 'null';
    if (type === 'Object' || type === 'Json') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <Sidebar />
        <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
          <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col p-6">
            <div className="animate-pulse">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div>
                  <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-6">
                <div className="bg-gray-200 rounded-xl h-64"></div>
                <div className="col-span-3 bg-gray-200 rounded-xl h-64"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <Sidebar />
        <div className="flex-1 ml-16 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Instance Not Found</h2>
            <p className="text-gray-500 mb-4">The process instance could not be found.</p>
            <button
              onClick={() => navigate('/processes')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Processes
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isRunning = instance.state === 'ACTIVE' && !instance.suspended;
  const progress = calculateProgress();
  const activityCounts = getActivityCounts();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/processes')}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h1 className="text-xl font-semibold text-gray-900">{instance.processDefinitionName}</h1>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getStatusColor(instance.state, instance.suspended)}`}>
                      {isRunning && <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />}
                      <span className="text-sm">{getStatusLabel(instance.state, instance.suspended)}</span>
                    </div>
                    {incidents.length > 0 && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-red-50 rounded-full">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">{incidents.length} Incident{incidents.length > 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      <span className="font-mono text-xs">{instance.id.substring(0, 8)}...</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(instance.startTime).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{calculateDuration(instance.startTime, instance.endTime)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchInstanceDetails()}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <RefreshCw size={18} />
                </button>
                {isRunning && (
                  <>
                    <button
                      onClick={handleSuspend}
                      disabled={suspending}
                      className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {suspending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Pause className="w-4 h-4" />
                      )}
                      Suspend
                    </button>
                    <button className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
                      <RotateCcw className="w-4 h-4" />
                      Retry
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={canceling}
                      className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {canceling ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Cancel
                    </button>
                  </>
                )}
                {/* Delete button - always visible */}
                <button
                  onClick={handleCancel}
                  disabled={canceling}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {canceling ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Delete Instance
                </button>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Sidebar - Instance Details */}
              <div className="space-y-4">
                {/* Progress Card */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-blue-100">Execution Progress</div>
                      <div className="text-2xl font-semibold">{progress}%</div>
                    </div>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-100">Completed</span>
                      <span>{activityCounts.completed} tasks</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-100">Active</span>
                      <span>{activityCounts.active} tasks</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-100">Remaining</span>
                      <span>{activityCounts.remaining} tasks</span>
                    </div>
                  </div>
                </div>

                {/* Time Metrics */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">Time Metrics</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-600">Total Duration</span>
                        <span className="text-sm font-medium text-gray-900">{calculateDuration(instance.startTime, instance.endTime)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: '70%' }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Process Instance Details */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Layers className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold text-gray-900">Instance Details</h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="text-gray-500 mb-1">Instance ID</div>
                      <div className="text-gray-900 font-mono text-xs break-all">{instance.id}</div>
                    </div>
                    {instance.businessKey && (
                      <div>
                        <div className="text-gray-500 mb-1">Business Key</div>
                        <div className="text-gray-900">{instance.businessKey}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-gray-500 mb-1">Definition</div>
                      <div className="text-gray-900">{instance.processDefinitionKey}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">Version</div>
                      <div className="text-gray-900">{instance.version || 1}</div>
                    </div>
                    {instance.tenantId && (
                      <div>
                        <div className="text-gray-500 mb-1">Tenant</div>
                        <div className="text-gray-900">{instance.tenantId}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-gray-500 mb-1">State</div>
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${getStatusColor(instance.state, instance.suspended)}`}>
                        {isRunning && <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" />}
                        {instance.state}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="lg:col-span-3 space-y-6">
                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex gap-6">
                    {[
                      { key: 'bpmn', label: 'BPMN Diagram', icon: Layers },
                      { key: 'diagram', label: 'Flow Diagram', icon: GitBranch },
                      { key: 'activities', label: 'Activities', count: activities.length, icon: Activity },
                      { key: 'variables', label: 'Variables', count: variables.length, icon: Database },
                      { key: 'incidents', label: 'Incidents', count: incidents.length, icon: AlertCircle },
                      { key: 'tasks', label: 'Tasks', count: tasks.length, icon: List },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key as any)}
                        className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key
                          ? 'text-blue-600 border-blue-600'
                          : 'text-gray-500 border-transparent hover:text-gray-700'
                          }`}
                      >
                        <tab.icon size={16} />
                        {tab.label}
                        {tab.count !== undefined && (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === tab.key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>


                {/* BPMN Diagram Tab */}
                {activeTab === 'bpmn' && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col h-[600px]">
                    <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                      <div>
                        <h3 className="font-semibold text-gray-900">BPMN Process Diagram</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Live execution state with token counters</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full border border-blue-200">Active</span>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full border border-gray-200">Completed</span>
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full border border-red-200">Incident</span>
                      </div>
                    </div>
                    <div className="flex-1 bg-gray-50 relative">
                      {!bpmnXml && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                      )}
                      <div ref={bpmnContainerRef} className="w-full h-full" />
                      <style>{`
                        .highlight-active:not(.djs-connection) .djs-visual > :nth-child(1) {
                          stroke: #3b82f6 !important;
                          stroke-width: 2px !important;
                          fill: #eff6ff !important;
                        }
                      `}</style>
                    </div>
                  </div>
                )}

                {/* Flow Diagram Tab */}
                {activeTab === 'diagram' && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                      <div>
                        <h3 className="font-semibold text-gray-900">Process Flow Diagram</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Visual execution timeline</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setZoom(Math.max(50, zoom - 10))}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <ZoomOut className="w-4 h-4 text-gray-600" />
                        </button>
                        <span className="text-sm text-gray-600 min-w-[50px] text-center">{zoom}%</span>
                        <button
                          onClick={() => setZoom(Math.min(150, zoom + 10))}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <ZoomIn className="w-4 h-4 text-gray-600" />
                        </button>
                        <div className="w-px h-6 bg-gray-300 mx-1" />
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <Maximize2 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                          <Download className="w-4 h-4 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    <div className="p-8 bg-gradient-to-br from-slate-50 to-white overflow-auto" style={{ minHeight: '500px', maxHeight: '600px' }}>
                      <div
                        className="flex flex-col items-center gap-0"
                        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center', transition: 'transform 0.2s' }}
                      >
                        {activities.map((activity, idx) => {
                          const isActive = activity.endTime === null && !activity.canceled;
                          const isCompleted = activity.endTime !== null && !activity.canceled;
                          const isCanceled = activity.canceled;
                          const isPending = !isActive && !isCompleted && !isCanceled;
                          const hasIncident = incidents.some(inc => inc.activityId === activity.activityId);

                          return (
                            <div key={activity.id} className="flex flex-col items-center">
                              {/* Step Node */}
                              <div className="flex items-center gap-4">
                                <div className="relative">
                                  {activity.activityType === 'startEvent' ? (
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${isCompleted
                                      ? 'bg-gray-800 shadow-lg'
                                      : isActive
                                        ? 'bg-gray-700 shadow-lg ring-4 ring-gray-200'
                                        : 'bg-gray-300'
                                      }`}>
                                      <Play className="w-6 h-6 text-white" />
                                    </div>
                                  ) : activity.activityType === 'endEvent' ? (
                                    <div className={`w-14 h-14 rounded-full flex items-center justify-center border-4 ${isCompleted
                                      ? 'border-gray-800 bg-gray-100'
                                      : isPending
                                        ? 'border-dashed border-gray-300 bg-gray-50'
                                        : 'border-gray-400 bg-white'
                                      }`}>
                                      <div className={`w-8 h-8 rounded-full ${isCompleted ? 'bg-gray-800' : 'bg-gray-400'
                                        }`} />
                                    </div>
                                  ) : (
                                    <div className={`w-52 h-20 rounded-xl flex items-center gap-3 px-4 transition-all ${isCompleted
                                      ? 'bg-white border-2 border-gray-400 shadow-md'
                                      : isActive
                                        ? 'bg-white border-2 border-gray-600 shadow-xl ring-4 ring-gray-100'
                                        : isCanceled
                                          ? 'bg-red-50 border-2 border-red-300'
                                          : 'bg-gray-50 border-2 border-dashed border-gray-300'
                                      }`}>
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isCompleted
                                        ? 'bg-gray-700'
                                        : isActive
                                          ? 'bg-gray-800'
                                          : isCanceled
                                            ? 'bg-red-500'
                                            : 'bg-gray-300'
                                        }`}>
                                        {getActivityIcon(activity.activityType)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-sm font-medium block truncate ${isCompleted
                                          ? 'text-gray-900'
                                          : isActive
                                            ? 'text-gray-900'
                                            : isCanceled
                                              ? 'text-red-900'
                                              : 'text-gray-400'
                                          }`}>
                                          {activity.activityName}
                                        </span>
                                        <span className={`text-xs ${isCompleted
                                          ? 'text-gray-600'
                                          : isActive
                                            ? 'text-gray-600'
                                            : isPending
                                              ? 'text-gray-400'
                                              : 'text-gray-400'
                                          }`}>
                                          {isPending ? 'Pending' : activity.activityType}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                                  {/* Incident Badge */}
                                  {hasIncident && (
                                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                                      <AlertCircle className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </div>

                                {/* Duration/Status Badge - positioned to the right */}
                                {activity.activityType !== 'startEvent' && activity.activityType !== 'endEvent' && (
                                  <div className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap font-medium ${isCompleted
                                    ? 'bg-gray-700 text-white'
                                    : isActive
                                      ? 'bg-gray-800 text-white flex items-center gap-1.5'
                                      : isPending
                                        ? 'bg-gray-100 text-gray-500 border border-dashed border-gray-300'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}>
                                    {isActive && (
                                      <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                    )}
                                    {isCompleted && activity.durationInMillis
                                      ? `${Math.round(activity.durationInMillis / 1000)}s`
                                      : isActive
                                        ? 'Running...'
                                        : isPending
                                          ? 'Waiting'
                                          : '-'
                                    }
                                  </div>
                                )}

                                {/* Node Label for start/end - positioned to the right */}
                                {(activity.activityType === 'startEvent' || activity.activityType === 'endEvent') && (
                                  <div className={`text-sm whitespace-nowrap font-medium ${isCompleted ? 'text-gray-800' : isPending ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {activity.activityName}
                                  </div>
                                )}
                              </div>

                              {/* Vertical Connector Arrow */}
                              {idx < activities.length - 1 && (
                                <div className="flex flex-col items-center my-1">
                                  <div className={`w-0.5 h-6 ${isCompleted
                                    ? 'bg-gray-600'
                                    : 'bg-gray-300'
                                    }`} />
                                  <div className={`w-0 h-0 border-t-[8px] border-x-4 border-x-transparent ${isCompleted
                                    ? 'border-t-gray-600'
                                    : 'border-t-gray-300'
                                    }`} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Activities Tab */}
                {activeTab === 'activities' && (
                  <div className="space-y-3">
                    {activities.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No activities found</div>
                    ) : (
                      activities.map((activity, index) => (
                        <div
                          key={activity.id}
                          className={`flex items-center gap-4 p-4 rounded-lg border ${activity.endTime === null
                            ? 'bg-blue-50 border-blue-200'
                            : activity.canceled
                              ? 'bg-red-50 border-red-200'
                              : 'bg-white border-gray-200'
                            }`}
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200">
                            {getActivityIcon(activity.activityType)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{activity.activityName}</span>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                {activity.activityType}
                              </span>
                              {activity.endTime === null && (
                                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                  In Progress
                                </span>
                              )}
                              {activity.canceled && (
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded">
                                  Canceled
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              Started: {new Date(activity.startTime).toLocaleString()}
                              {activity.endTime && (
                                <> • Ended: {new Date(activity.endTime).toLocaleString()}</>
                              )}
                              {activity.durationInMillis && (
                                <> • Duration: {Math.round(activity.durationInMillis / 1000)}s</>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-400">
                            #{index + 1}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Variables Tab */}
                {activeTab === 'variables' && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-white border-b border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Database className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">Process Variables</h3>
                            <p className="text-sm text-gray-500">
                              {variablesLoading ? 'Loading...' : `Showing ${filteredVariables.length} of ${totalVariableCount} variables`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search variables by name, type, or value..."
                          value={variableSearch}
                          onChange={(e) => {
                            setVariableSearch(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Variable Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Value</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredVariables.length > 0 ? (
                            filteredVariables.map((variable: Variable, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <code className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                                    {variable.name}
                                  </code>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-gray-600">{variable.type}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <code className="text-sm text-gray-900 font-mono break-all max-w-xs block">
                                    {formatValue(variable.value, variable.type)}
                                  </code>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button className="p-1 text-gray-400 hover:text-blue-600">
                                      <Eye size={14} />
                                    </button>
                                    {isRunning && (
                                      <button className="p-1 text-gray-400 hover:text-blue-600">
                                        <Edit size={14} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                                {variableSearch ? `No variables found matching "${variableSearch}"` : 'No variables found'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalVariableCount > 0 && totalPages > 1 && (
                      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, totalVariableCount)} of {totalVariableCount} variables
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft className="w-4 h-4 text-gray-600" />
                          </button>

                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${currentPage === page
                                  ? 'bg-purple-600 text-white'
                                  : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                {page}
                              </button>
                            ))}
                          </div>

                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Incidents Tab */}
                {activeTab === 'incidents' && (
                  <div className="space-y-3">
                    {incidents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No incidents found</div>
                    ) : (
                      incidents.map((incident) => (
                        <div key={incident.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-red-900">{incident.incidentType}</span>
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded">
                                  {incident.activityId}
                                </span>
                              </div>
                              <p className="text-sm text-red-700 mb-2">{incident.incidentMessage}</p>
                              <div className="text-xs text-red-500">
                                Created: {new Date(incident.createTime).toLocaleString()}
                              </div>
                            </div>
                            <button className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700">
                              Resolve
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                  <div className="space-y-3">
                    {tasks.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">No active tasks</div>
                    ) : (
                      tasks.map((task) => (
                        <div
                          key={task.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition-all"
                          onClick={() => navigate(`/tasks?taskId=${task.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <User size={18} className="text-green-600" />
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{task.name}</div>
                                <div className="text-sm text-gray-500">
                                  {task.assignee ? `Assigned to ${task.assignee}` : 'Unassigned'}
                                  {task.due && <> • Due: {new Date(task.due).toLocaleDateString()}</>}
                                </div>
                              </div>
                            </div>
                            <ChevronRight size={18} className="text-gray-400" />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessInstancePage;
